import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

/** How to order a table by one column, ascending. */
export type Column<T> = {
  compare: (a: T, b: T) => number
  /** Counts read best largest first; names alphabetically. */
  descFirst?: boolean
}

/**
 * A table's order, kept in the URL like everything else on this site, so a
 * link to "drivers by poles" opens sorted by poles. `?sort=poles` or
 * `?sort=-poles`; anything unrecognised falls back to the default.
 */
export function useSort<T, K extends string>(
  rows: T[],
  columns: Record<K, Column<T>>,
  initial: NoInfer<K>,
) {
  const [params, setParams] = useSearchParams()
  const raw = params.get('sort') ?? ''
  const name = raw.replace(/^-/, '')
  const key = (name in columns ? name : initial) as K
  const desc = name in columns ? raw.startsWith('-') : (columns[initial].descFirst ?? false)

  const sorted = useMemo(() => {
    const { compare } = columns[key]
    return [...rows].sort((a, b) => (desc ? compare(b, a) : compare(a, b)))
  }, [rows, columns, key, desc])

  function toggle(next: K) {
    const nextDesc = next === key ? !desc : (columns[next].descFirst ?? false)
    const updated = new URLSearchParams(params)
    updated.set('sort', `${nextDesc ? '-' : ''}${next}`)
    setParams(updated, { replace: true })
  }

  return { sorted, key, desc, toggle }
}

/** Ascending comparison for a plain number or string field. */
export function by<T>(
  read: (row: T) => number | string | null | undefined,
): (a: T, b: T) => number {
  return (a, b) => {
    const left = read(a)
    const right = read(b)
    if (left === right) return 0
    if (left === null || left === undefined) return -1
    if (right === null || right === undefined) return 1
    return typeof left === 'number' && typeof right === 'number'
      ? left - right
      : String(left).localeCompare(String(right))
  }
}
